// MemberDashboard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  FormControl, InputLabel, MenuItem, Select
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PaymentIcon from "@mui/icons-material/Payment";
import DescriptionIcon from "@mui/icons-material/Description";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CampaignIcon from "@mui/icons-material/Campaign";
import { Line } from "react-chartjs-2";
import { motion } from "framer-motion";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler
} from "chart.js";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchSingleMemberDetails,
  fetchMemberTransactions,
} from "../../services/memberService";
import { fetchGroupTerms } from "../../services/groupService";
import { fetchSystemSettings } from "../../services/systemSettingsService";
import { fetchDeskOfficerNotifications } from "../../services/deskOfficerService";
import { getMemberLoans } from "../../services/loanService";
import Footer from "../Footer";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const formatCurrency = (value, locale = "en-TZ") =>
  typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : value;

const getWeekStart = (date) => {
  const copy = new Date(date);
  const day = copy.getDay();
  const diff = (day + 6) % 7;
  copy.setDate(copy.getDate() - diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const buildWeekBuckets = (count, locale = "en-TZ") => {
  const buckets = [];
  const today = getWeekStart(new Date());
  for (let offset = count - 1; offset >= 0; offset -= 1) {
    const bucket = new Date(today);
    bucket.setDate(today.getDate() - offset * 7);
    buckets.push({
      key: bucket.toISOString().slice(0, 10),
      label: bucket.toLocaleDateString(locale, { month: "short", day: "numeric" }),
    });
  }
  return buckets;
};

const buildWeeklySeries = (transactions = [], count = 5, locale = "en-TZ") => {
  const buckets = buildWeekBuckets(count, locale);
  const contributions = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});
  const disbursements = buckets.reduce((acc, bucket) => ({ ...acc, [bucket.key]: 0 }), {});

  transactions.forEach((tx) => {
    if (!tx?.created_at) return;
    const start = getWeekStart(new Date(tx.created_at));
    const key = start.toISOString().slice(0, 10);
    if (!Object.prototype.hasOwnProperty.call(contributions, key)) return;
    const amount = Math.abs(Number(tx.amount) || 0);
    if (["MEMBER_CONTRIBUTION", "CONTRIBUTION"].includes(tx.transaction_type)) {
      contributions[key] += amount;
    } else if (["LOAN_DISBURSEMENT", "LOAN_REPAYMENT", "MEMBER_WITHDRAWAL", "WITHDRAWAL"].includes(tx.transaction_type)) {
      disbursements[key] += amount;
    }
  });

  return {
    labels: buckets.map((bucket) => bucket.label),
    contributions: buckets.map((bucket) => contributions[bucket.key] || 0),
    disbursements: buckets.map((bucket) => disbursements[bucket.key] || 0),
  };
};

const getPeriodWindowFromDueDate = (dueDate, frequency) => {
  if (!dueDate || !frequency) {
    return { start: null, end: null };
  }
  const end = new Date(dueDate);
  if (Number.isNaN(end.getTime())) {
    return { start: null, end: null };
  }
  const start = new Date(end);
  if (frequency === "Weekly") {
    start.setDate(end.getDate() - 7);
  } else if (frequency === "Monthly") {
    start.setMonth(end.getMonth() - 1);
  } else {
    return { start: null, end };
  }
  return { start, end };
};

const formatDateTime = (value, locale = "en-TZ") => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
};

const formatTimeAgo = (timestamp, t) => {
  if (!timestamp) return "";
  const created = new Date(timestamp);
  if (Number.isNaN(created.getTime())) return "";
  const diffMs = Date.now() - created.getTime();
  if (diffMs < 0) return t("just now");
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return t("{{count}} seconds ago", { count: Math.round(diffMs / 1000) });
  if (diffMs < hour) return t("{{count}} minutes ago", { count: Math.round(diffMs / minute) });
  if (diffMs < day) return t("{{count}} hours ago", { count: Math.round(diffMs / hour) });
  return t("{{count}} days ago", { count: Math.round(diffMs / day) });
};

const normalizeListResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const brand = "#800b33";
const brandHover = "#6c0a2b";

const formatNumber = (n, locale = "en-TZ") =>
  typeof n === "number" ? new Intl.NumberFormat(locale).format(n) : n;

const kFormatter = (value) => {
  if (typeof value !== "number") return value;
  return `${Math.round(value / 1000)} K`;
};

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const StatCard = ({ icon, label, value, index, locale }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: 3,
        boxShadow: 3,
        "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
        transition: "all .25s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: `${brand}1A`,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {formatNumber(value, locale)}
          </Typography>
        </Box>
      </Box>
    </Card>
  </motion.div>
);

const MemberDashboard = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const { memberId } = useAuth();
  const navigate = useNavigate();
  const handleOpenContributions = useCallback(() => navigate("/app/member/contributions"), [navigate]);
  const handleRequestLoan = useCallback(() => navigate("/app/loan-approvals"), [navigate]);
  const handleViewAnnouncements = useCallback(() => navigate("/app/announcement"), [navigate]);
  const [memberDetails, setMemberDetails] = useState(null);
  const [groupSettings, setGroupSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [memberLoans, setMemberLoans] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("current");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    fetchGroupTerms()
      .then((payload) => {
        if (!active) return;
        setTerms(normalizeListResponse(payload));
      })
      .catch((err) => {
        if (active) {
          console.error("Failed to load terms:", err);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetchSystemSettings()
      .then((payload) => {
        if (!active) return;
        setGroupSettings(payload);
      })
      .catch((err) => {
        if (active) {
          console.error("Failed to load group settings:", err);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const activeTermId = useMemo(
    () => terms.find((term) => term.status === "ACTIVE")?.id || null,
    [terms]
  );
  const effectiveTermId = useMemo(() => {
    if (selectedTermId === "all") return null;
    if (selectedTermId === "current") return activeTermId || terms[0]?.id || null;
    return selectedTermId;
  }, [selectedTermId, activeTermId, terms]);
  const isCurrentTermView = useMemo(() => {
    if (selectedTermId === "current") return true;
    if (selectedTermId === "all") return false;
    if (activeTermId === null || activeTermId === undefined) return false;
    return String(selectedTermId) === String(activeTermId);
  }, [selectedTermId, activeTermId]);

  const handleViewStatement = useCallback(() => {
    if (!memberId) return;
    const params = new URLSearchParams({ tab: "0", memberId });
    if (effectiveTermId) {
      params.set("termId", effectiveTermId);
    }
    navigate(`/app/finance/transactions-audit?${params.toString()}`);
  }, [memberId, navigate, effectiveTermId]);

  useEffect(() => {
    if (!memberId) {
      setError("Member context not available.");
      setLoading(false);
      return;
    }
    if (selectedTermId === "current" && !effectiveTermId) {
      return undefined;
    }
    const controller = new AbortController();
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const transactionParams = effectiveTermId ? { term_id: effectiveTermId } : {};
        if (process.env.NODE_ENV !== "production") {
          console.debug("MemberDashboard transactions filter", transactionParams);
        }
        const loanParams = effectiveTermId ? { term_id: effectiveTermId } : {};
        const [
          memberDetailsPayload,
          transactionPayload,
          notificationPayload,
          loansPayload,
        ] = await Promise.all([
          fetchSingleMemberDetails(memberId),
          fetchMemberTransactions(memberId, transactionParams),
          fetchDeskOfficerNotifications({ signal: controller.signal }),
          getMemberLoans(memberId, loanParams),
        ]);

        setMemberDetails(memberDetailsPayload);
        setTransactions(normalizeListResponse(transactionPayload));
        setNotifications(normalizeListResponse(notificationPayload));
        setMemberLoans(normalizeListResponse(loansPayload));
        setError(null);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("MemberDashboard error:", err);
          setError(err.message || "Failed to load member data.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
    return () => controller.abort();
  }, [memberId, effectiveTermId]);

  const contributionsSum = useMemo(() => {
    return transactions
      .filter((tx) => ["MEMBER_CONTRIBUTION", "CONTRIBUTION"].includes(tx.transaction_type))
      .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0);
  }, [transactions]);

  const memberBalance = parseNumericValue(memberDetails?.balance);
  const overpaymentBalance = parseNumericValue(memberDetails?.overpayment_balance);
  const contributionsToShow = effectiveTermId ? contributionsSum : (memberBalance ?? contributionsSum);
  const prepaidBalance = parseNumericValue(memberDetails?.prepaid_contribution_balance) ?? 0;
  const contributionMode = groupSettings?.contribution_handling_mode || "PAYG_NO_PREPAY";

  const minimumPerPeriod = useMemo(() => {
    if (!memberDetails?.group) return null;
    const perPeriod = Number(memberDetails.group.contribution_amount ?? 0);
    const slots = Number(memberDetails.number_of_slots ?? 1);
    if (!Number.isFinite(perPeriod) || !Number.isFinite(slots)) return null;
    return perPeriod * slots;
  }, [memberDetails]);

  const periodWindow = useMemo(() => {
    return getPeriodWindowFromDueDate(
      memberDetails?.next_due_date,
      memberDetails?.group?.frequency
    );
  }, [memberDetails]);

  const paidForPeriod = useMemo(() => {
    if (!isCurrentTermView) return null;
    if (!periodWindow.start || !periodWindow.end) return null;
    const startTs = periodWindow.start.getTime();
    const endTs = periodWindow.end.getTime();
    if (Number.isNaN(startTs) || Number.isNaN(endTs)) return null;
    return transactions
      .filter((tx) => ["MEMBER_CONTRIBUTION", "CONTRIBUTION"].includes(tx.transaction_type))
      .reduce((sum, tx) => {
        const createdAt = new Date(tx.created_at);
        const ts = createdAt.getTime();
        if (Number.isNaN(ts)) return sum;
        if (ts < startTs || ts > endTs) return sum;
        return sum + Math.abs(Number(tx.amount) || 0);
      }, 0);
  }, [transactions, periodWindow, isCurrentTermView]);

  const remainingToMinimum = useMemo(() => {
    if (minimumPerPeriod === null || paidForPeriod === null) return null;
    const prepayOffset =
      contributionMode === "PREPAY_ADVANCE_ALLOWED"
        ? Math.min(prepaidBalance, minimumPerPeriod)
        : 0;
    return Math.max(minimumPerPeriod - (paidForPeriod + prepayOffset), 0);
  }, [minimumPerPeriod, paidForPeriod, contributionMode, prepaidBalance]);

  const periodLabel = useMemo(() => {
    if (!isCurrentTermView) return "—";
    if (!periodWindow.start || !periodWindow.end) return "—";
    return `${periodWindow.start.toLocaleDateString(locale, { month: "short", day: "numeric" })} - ${periodWindow.end.toLocaleDateString(locale, { month: "short", day: "numeric" })}`;
  }, [periodWindow, locale, isCurrentTermView]);

  const disbursementSum = useMemo(
    () =>
      transactions
        .filter((tx) => ["LOAN_DISBURSEMENT", "MEMBER_WITHDRAWAL", "WITHDRAWAL"].includes(tx.transaction_type))
        .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0),
    [transactions]
  );

  const repaymentSum = useMemo(
    () =>
      transactions
        .filter((tx) => tx.transaction_type === "LOAN_REPAYMENT")
        .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0),
    [transactions]
  );

  const penaltySum = useMemo(
    () =>
      transactions
        .filter((tx) => tx.transaction_type === "PENALTY")
        .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0),
    [transactions]
  );

  const dividendSum = useMemo(
    () =>
      transactions
        .filter((tx) => tx.transaction_type === "DIVIDEND_PAYOUT")
        .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0),
    [transactions]
  );

  const loanDisbursementSum = useMemo(
    () =>
      transactions
        .filter((tx) => tx.transaction_type === "LOAN_DISBURSEMENT")
        .reduce((sum, tx) => sum + Math.abs(Number(tx.amount) || 0), 0),
    [transactions]
  );

  const loanBalanceFromLoans = useMemo(() => {
    if (!memberLoans.length) return null;
    const outstandingValues = memberLoans
      .map((loan) => parseNumericValue(loan?.outstanding_amount))
      .filter((value) => value !== null);
    if (!outstandingValues.length) return null;
    return outstandingValues.reduce((sum, value) => sum + value, 0);
  }, [memberLoans]);

  const loanBalanceFromApi = [
    memberDetails?.loan_balance,
    memberDetails?.loan_balance_total,
    memberDetails?.outstanding_loan_amount,
  ]
    .map(parseNumericValue)
    .find((value) => value !== null);
  const loanBalance =
    loanBalanceFromLoans ??
    loanBalanceFromApi ??
    Math.max(loanDisbursementSum - repaymentSum, 0);

  const stats = useMemo(
    () => [
      {
        label: effectiveTermId ? t("Current Term Contributions (TZS)") : t("Total Contributions (TZS)"),
        value: contributionsToShow,
        icon: <AccountBalanceWalletIcon sx={{ fontSize: 28, color: brand }} />,
      },
      {
        label: t("Loan Balance (TZS)"),
        value: loanBalance,
        icon: <MonetizationOnIcon sx={{ fontSize: 28, color: brand }} />,
      },
      {
        label: t("Penalties (TZS)"),
        value: penaltySum,
        icon: <ErrorOutlineIcon sx={{ fontSize: 28, color: brand }} />,
      },
      {
        label: t("Dividends (TZS)"),
        value: dividendSum,
        icon: <EmojiEventsIcon sx={{ fontSize: 28, color: brand }} />,
      },
    ],
    [contributionsToShow, loanBalance, penaltySum, dividendSum, effectiveTermId, t]
  );

  const chartSeries = useMemo(() => buildWeeklySeries(transactions, 5, locale), [transactions, locale]);
  const chartData = useMemo(
    () => ({
      labels: chartSeries.labels,
      datasets: [
        {
          label: "Contributions (TZS)",
          data: chartSeries.contributions,
          borderColor: brand,
          backgroundColor: `${brand}24`,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
        {
          label: "Disbursements (TZS)",
          data: chartSeries.disbursements,
          borderColor: "#bdbdbd",
          backgroundColor: "rgba(189,189,189,.18)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
      ],
    }),
    [chartSeries]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (val) => kFormatter(val),
        },
        grid: { color: "rgba(0,0,0,0.06)" },
      },
      x: { grid: { display: false } },
    },
  };

  const reminders = useMemo(() => {
    const list = [];
    if (memberDetails?.next_due_date) {
      list.push({
        id: "contribution",
        type: "Contribution",
        message: `${t('Next contribution')}: ${formatDateTime(memberDetails.next_due_date, locale)}`,
        status: "Upcoming",
      });
    }
    const nextLoanDue = memberDetails?.next_loan_due_date || memberDetails?.loan?.next_payment_due;
    if (nextLoanDue) {
      list.push({
        id: "loan",
        type: "Repayment",
        message: `${t('Loan installment due')}: ${formatDateTime(nextLoanDue, locale)}`,
        status: "Upcoming",
      });
    }
    const latePenalty = transactions.find((tx) => tx.transaction_type === "PENALTY" && Number(tx.amount) > 0);
    if (latePenalty) {
      list.push({
        id: "penalty",
        type: "Penalty",
        message: `${latePenalty.description || t("Late fee applied")} (${formatCurrency(
          Number(latePenalty.amount) || 0,
          locale
        )} TZS)`,
        status: "Overdue",
      });
    }
    return list;
  }, [memberDetails, transactions]);

  const recentTransactions = useMemo(
    () =>
      [...transactions]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5)
        .map((tx) => ({
          id: tx.id,
          type: tx.transaction_type,
          amount: Number(tx.amount) || 0,
          description: tx.description || tx.note || tx.transaction_type,
          date: tx.created_at,
        })),
    [transactions]
  );

  const upcomingNotifications = useMemo(
    () =>
      notifications.map((notification) => ({
        id: notification.id || notification.pk || `${notification.type}-${notification.created_at}`,
        text: notification.message || notification.text || notification.title || t("Notification"),
        time: formatTimeAgo(notification.created_at || notification.timestamp, t),
        color: notification.color || brand,
      })),
    [notifications]
  );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", justifyContent: "center" }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {t('Member Dashboard')}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t('Track your savings, loans, and performance')}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("Term")}</InputLabel>
          <Select
            value={selectedTermId}
            label={t("Term")}
            onChange={(event) => setSelectedTermId(event.target.value)}
          >
            <MenuItem value="current">{t("Current term")}</MenuItem>
            <MenuItem value="all">{t("All terms")}</MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name || `${t("Term")} ${term.term_number}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={stat.label}>
            <StatCard icon={stat.icon} label={stat.label} value={stat.value} index={index} locale={locale} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('My Weekly Contributions (TZS, in K)')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('Last 5 weeks — contributions vs captured disbursements')}
                  </Typography>
                </Box>
                <Chip
                  label={t("Live data")}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: "success.main",
                    color: "#fff",
                  }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Line data={chartData} options={chartOptions} />
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.2}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                {t('Quick Actions')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                  onClick={handleOpenContributions}
                >
                  {t('Make Contribution')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                  onClick={handleViewStatement}
                >
                  {t('View My Statement')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RequestQuoteIcon />}
                  sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                  onClick={handleRequestLoan}
                >
                  {t('Request Loan')}
                </Button>
              </Box>
            </Card>
          </motion.div>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.3}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3, mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t("Contribution Status")}
                </Typography>
                <Chip
                  size="small"
                  label={
                    contributionMode === "PREPAY_ADVANCE_ALLOWED"
                      ? t("Prepaid")
                      : t("Pay-As-You-Go")
                  }
                  color={contributionMode === "PREPAY_ADVANCE_ALLOWED" ? "success" : "default"}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "grid", gap: 1.2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("Current period")}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {periodLabel}
                  </Typography>
                </Box>
                {!isCurrentTermView && (
                  <Typography variant="caption" color="text.secondary">
                    {t("Switch to the current term to view period progress.")}
                  </Typography>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("Minimum due")}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {minimumPerPeriod === null ? "—" : `${formatNumber(minimumPerPeriod, locale)} TZS`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("Paid so far")}
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {paidForPeriod === null ? "—" : `${formatNumber(paidForPeriod, locale)} TZS`}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("Remaining to minimum")}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={
                      remainingToMinimum === null
                        ? "text.primary"
                        : remainingToMinimum > 0
                          ? "warning.main"
                          : "success.main"
                    }
                  >
                    {remainingToMinimum === null ? "—" : `${formatNumber(remainingToMinimum, locale)} TZS`}
                  </Typography>
                </Box>
                {contributionMode === "PREPAY_ADVANCE_ALLOWED" ? (
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("Prepaid credit")}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {formatNumber(prepaidBalance, locale)} TZS
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t("Extra deposits stay as savings and do not prepay future periods.")}
                  </Typography>
                )}
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={7}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                  {t('Upcoming Deadlines & Reminders')}
                </Typography>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t('Type')}</TableCell>
                        <TableCell>{t('Message')}</TableCell>
                        <TableCell>{t('Status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reminders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary">
                              {t('No upcoming reminders at the moment.')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        reminders.map((reminder, idx) => (
                          <TableRow key={reminder.id} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{reminder.type}</TableCell>
                            <TableCell>{reminder.message}</TableCell>
                            <TableCell>
                              <Chip
                                label={reminder.status}
                                color={reminder.status === "Upcoming" ? "success" : "error"}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={5}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.6}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                  {t('layout.notificationsTitle')}
                </Typography>
                {upcomingNotifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('No new notifications')}
                  </Typography>
                ) : (
                  upcomingNotifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      whileHover={{ scale: 1.02 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px",
                        borderRadius: "10px",
                        background: "#f9fafb",
                        marginBottom: "10px",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <NotificationsActiveIcon sx={{ color: notification.color, marginRight: 12 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {notification.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.time}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {recentTransactions.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.8}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                    {t('Recent Transactions')}
                  </Typography>
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>{t('Type')}</TableCell>
                          <TableCell>{t('Description')}</TableCell>
                          <TableCell>{t('Amount')}</TableCell>
                          <TableCell>{t('Date')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentTransactions.map((transaction, idx) => (
                          <TableRow key={transaction.id} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{transaction.type}</TableCell>
                            <TableCell>{transaction.description}</TableCell>
                            <TableCell>{formatCurrency(transaction.amount, locale)}</TableCell>
                            <TableCell>{formatDateTime(transaction.date, locale)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      )}

      <Footer />
    </Box>
  );
};

export default MemberDashboard;
